import React from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import BoappaLogo from '../../../assets/icons/boappa.svg'
import { theme } from '../../../theme/theme'
import { useDocumentTitle } from '../../../hooks/useDocumentTitle'
import { reverseDocumentTitle } from '../../../navigation/reverseDocumentTitle'
import { useLinkToken } from '../../../hooks/useLinkToken'
import { useLinkLiveness } from '../../../hooks/useLinkLiveness'
import { isRateLimited } from '../../../helpers/rate_limit'
import { SpinnerWrapper } from '../../../components/common/SpinnerWrapper'
import { useToastNotifications } from '../../../hooks/useToastNotification'
import { ToastType } from '../../../components/common/Toast/toast-type'
import {
  Illustration,
  IllustrationChoices,
} from '../../../components/common/Illustration'
import { resetPassword } from '../../../api/users'
import { useAuth } from '../../../hooks/useAuth'
import { PasswordForm } from '../../../components/authentication/PasswordForm'
import { reverseUrl } from '../../../navigation/reverseUrl'
import {
  setSecureBearerCookies,
  setSecureUserIdCookie,
} from '../../../helpers/authentication'

export const RetrievePasswordView = (): React.ReactNode => {
  const { translate } = useAppTranslation()
  useDocumentTitle(reverseDocumentTitle('reset-password'))
  const { setToastNotification } = useToastNotifications()
  const { setUserId } = useAuth()
  const navigate = useNavigate()
  const client = useQueryClient()
  const [linkDied, setLinkDied] = React.useState(false)

  const token = useLinkToken()

  const liveness = useLinkLiveness(token)

  const { mutate: mutateResetPassword, isPending } = useMutation({
    mutationFn: async (variables: { token: string; password: string }) => {
      const response = await resetPassword(variables.token, variables.password)
      return response.data.data
    },
    onSuccess: async (data) => {
      setToastNotification(
        ToastType.SUCCESS,
        translate(
          'authenticationRetrievePasswordView.flashMessage.passwordResetSuccess'
        )
      )
      setSecureBearerCookies(data.token)
      setSecureUserIdCookie(data.user._id)
      setUserId(data.user._id)
      navigate(reverseUrl('feed'))
      client.resetQueries()
    },
    onError: (error) => {
      if (isRateLimited(error)) {
        setToastNotification(
          ToastType.DANGER,
          translate('flashMessage.tooManyAttemptsTryLaterError')
        )
        return
      }
      if (!(isAxiosError(error) && error.response?.status === 400)) {
        setToastNotification(
          ToastType.DANGER,
          translate('flashMessage.somethingWentWrongTryAgainError')
        )
        return
      }

      setToastNotification(
        ToastType.DANGER,
        translate(
          'authenticationRetrievePasswordView.flashMessage.passwordResetFailure'
        )
      )
      setLinkDied(true)
    },
  })

  if (liveness === 'checking') {
    return <SpinnerWrapper />
  }

  if (token == null || token.length === 0 || liveness === 'dead' || linkDied) {
    return (
      <Navigate
        to={reverseUrl('reset-password')}
        replace
        state={{ linkWasDead: true }}
      />
    )
  }

  const onResetPassword = async (password: string): Promise<void> => {
    mutateResetPassword({ password, token })
  }

  return (
    <div className="flex h-full flex-col md:flex-row">
      <BoappaLogo
        className="absolute inset-x-8 w-16 md:w-40"
        alt="boappa logo"
      />
      <div className="mt-24 flex flex-1 justify-center overflow-y-auto md:mt-0 md:items-center">
        <div className="flex w-[90%] flex-col gap-4 md:w-[80%] xl:w-[60%]">
          <p style={theme.textVariants.h1}>
            {translate('authenticationResetPasswordView.title')}
          </p>
          <p style={theme.textVariants.paragraph}>
            {translate('authenticationRetrievePasswordView.subtitle')}
          </p>
          <PasswordForm
            setPassword={onResetPassword}
            isSettingPassword={isPending}
            passwordLabel={translate(
              'authenticationRetrievePasswordView.form.labels.password'
            )}
            submitLabel={translate(
              'authenticationRetrievePasswordView.retrievePasswordButtonTitle'
            )}
            sendNewLinkUrl={reverseUrl('reset-password')}
          />
        </div>
      </div>
      <div
        style={{ backgroundColor: theme.colors.brandLightGreen }}
        className="flex hidden h-40 items-center justify-center md:relative md:h-full md:flex-1 lg:flex"
      >
        <Illustration
          illustrationChoice={IllustrationChoices.SUN}
          width={400}
        />
      </div>
    </div>
  )
}
